import { IngredientWithDosing, ProductItem } from '../shoppingCart/types';
import { useContext, useCallback } from 'react';
import { ProductContext } from './ProductContext';
import { Commerce } from '@kotipizzagroup/kotipizza-shared-types';
import { OrderItem } from '../../model/order/OrderItem';
import { useAllIngredients } from '../../state/ingredient/hooks/useAllIngredients';
import { ErrorMessage } from '../../model/errors/ErrorMessage';
import { errorCode } from '../../model/errors';
import { useAddError } from '../error/ErrorContext';
import { useQueryClient } from '@tanstack/react-query';
import { useAllProductItems } from '../../state/product/hooks/useAllProductItems';
import { analyticsEvents, AnalyticsIngredientListName } from '../../utils/analytics/analytics';

export const useChangeItemQuantity = (): ((
  quantity: number
) => ErrorMessage<errorCode, Record<string, unknown>>[]) => {
  const { itemRef, setItem } = useContext(ProductContext);
  const queryClient = useQueryClient();

  return useCallback(
    (quantity: number) => {
      if (!itemRef.current) {
        return [];
      }

      const newItem = itemRef.current.clone();
      const newQuantity = newItem.quantity + quantity;
      if (newQuantity < 1) {
        throw new Error('Can not set quantity below 1');
      }

      queryClient.cancelQueries({ queryKey: ['product-price'] });

      newItem.quantity = newQuantity;
      const errors = newItem.validate();
      Object.freeze(newItem);
      setItem(newItem);
      return errors;
    },
    [itemRef, queryClient, setItem]
  );
};

export const useValidateOrderItem = (): (() => ErrorMessage<
  errorCode,
  Record<string, unknown>
>[]) => {
  const { itemRef, setItem } = useContext(ProductContext);

  return useCallback(() => {
    if (!itemRef.current) {
      return [];
    }

    const newItem = itemRef.current.clone();
    const errors = newItem.validate();
    Object.freeze(newItem);
    setItem(newItem);
    return errors;
  }, [itemRef, setItem]);
};

export const useRemoveIngredient = (): ((
  ingredientId: number,
  quantity?: number | undefined,
  blockId?: AnalyticsIngredientListName | undefined
) => void) => {
  const { itemRef, setItem } = useContext(ProductContext);
  const { ingredients } = useAllIngredients();
  const queryClient = useQueryClient();

  return useCallback(
    (
      ingredientId: number,
      quantity: number | undefined = 1,
      blockId: AnalyticsIngredientListName | undefined = AnalyticsIngredientListName.Ingredients
    ) => {
      if (!itemRef.current) {
        return;
      }

      const ingredientToRemove = ingredients.find(
        (i) => i.ingredientID === ingredientId && i.productSizeId === itemRef.current?.sizeId
      );

      if (!ingredientToRemove) {
        return;
      }

      queryClient.cancelQueries({ queryKey: ['product-price'] });

      ingredientToRemove.quantity = quantity;

      const newItem = itemRef.current.clone();
      if (newItem.getIngredientQuantity(ingredientToRemove) - ingredientToRemove.quantity > 0) {
        analyticsEvents.changeIngredientQuantity(ingredientToRemove.name);
      } else {
        analyticsEvents.removeIngredient(ingredientToRemove.name, blockId);
      }

      newItem.removeIngredient(ingredientToRemove);
      Object.freeze(newItem);
      setItem(newItem);
    },
    [ingredients, itemRef, queryClient, setItem]
  );
};

export const useAddIngredient = (): ((
  ingredientId: number,
  quantity?: number | undefined,
  blockId?: AnalyticsIngredientListName | undefined
) => void) => {
  const { itemRef, setItem } = useContext(ProductContext);
  const { ingredients } = useAllIngredients();
  const queryClient = useQueryClient();

  return useCallback(
    (
      ingredientId: number,
      quantity: number | undefined = 1,
      blockId: AnalyticsIngredientListName = AnalyticsIngredientListName.Ingredients
    ) => {
      if (!itemRef.current) {
        return;
      }

      const ingredientToAdd = ingredients.find(
        (i) => i.ingredientID === ingredientId && i.productSizeId === itemRef.current?.sizeId
      );

      if (!ingredientToAdd) {
        return;
      }

      queryClient.cancelQueries({ queryKey: ['product-price'] });

      ingredientToAdd.quantity = quantity;

      const newItem = itemRef.current.clone();
      if (newItem.getIngredientQuantity(ingredientToAdd) < 1) {
        analyticsEvents.addIngredient(ingredientToAdd.name, blockId);
      } else {
        analyticsEvents.changeIngredientQuantity(ingredientToAdd.name);
      }
      newItem.addIngredient({ ...ingredientToAdd, addedFrom: blockId });
      Object.freeze(newItem);
      setItem(newItem);
    },
    [ingredients, itemRef, queryClient, setItem]
  );
};

export const useAddBundleItem = (): ((
  product: ProductItem,
  quantity?: number | undefined,
  blockId?: AnalyticsIngredientListName | undefined
) => void) => {
  const { itemRef, setItem } = useContext(ProductContext);

  return useCallback(
    (
      product: ProductItem,
      quantity: number | undefined = 1,
      blockId: AnalyticsIngredientListName = AnalyticsIngredientListName.Ingredients
    ) => {
      const item = itemRef.current;

      if (!item) {
        return;
      }

      const newItem = item.clone();

      const index = newItem.bundleItems.findIndex(
        (i) => i.productID === product.productID && i.sizeId === product.sizeId
      );
      if (index >= 0) {
        newItem.bundleItems = newItem.bundleItems.map((bundleItem, i) =>
          i === index ? { ...bundleItem, quantity: bundleItem.quantity + quantity } : bundleItem
        );
        analyticsEvents.changeIngredientQuantity(product.name);
      } else {
        newItem.bundleItems = [...newItem.bundleItems, { ...product, quantity }];
        analyticsEvents.addIngredient(product.name, blockId);
      }

      Object.freeze(newItem);
      setItem(newItem);
    },
    [itemRef, setItem]
  );
};

export const useRemoveBundleItem = (): ((
  product: ProductItem,
  quantity?: number | undefined,
  blockId?: AnalyticsIngredientListName | undefined
) => void) => {
  const { itemRef, setItem } = useContext(ProductContext);

  return useCallback(
    (
      product: ProductItem,
      quantity: number | undefined = 1,
      blockId: AnalyticsIngredientListName = AnalyticsIngredientListName.Ingredients
    ) => {
      const item = itemRef.current;

      if (!item) return;

      const newItem = item.clone();

      const index = newItem.bundleItems.findIndex(
        (i) => i.productID === product.productID && i.sizeId === product.sizeId
      );
      if (index < 0) return;

      const newQuantity = newItem.bundleItems[index].quantity - quantity;

      if (newQuantity < 1) {
        newItem.bundleItems = newItem.bundleItems.filter((_, i) => i !== index);
        analyticsEvents.removeIngredient(product.name, blockId);
      } else {
        newItem.bundleItems = newItem.bundleItems.map((bundleItem, i) =>
          i === index ? { ...bundleItem, quantity: newQuantity } : bundleItem
        );
        analyticsEvents.changeIngredientQuantity(product.name);
      }

      Object.freeze(newItem);
      setItem(newItem);
    },
    [itemRef, setItem]
  );
};

export const useChangeIngredientSlotSelection = (): ((
  ingredient: IngredientWithDosing | null | undefined,
  slotId: string,
  currentIngredient: IngredientWithDosing | null
) => void) => {
  const { itemRef, setItem } = useContext(ProductContext);
  const queryClient = useQueryClient();

  return useCallback(
    (
      ingredient: IngredientWithDosing | null | undefined,
      slotId: string,
      currentIngredient: IngredientWithDosing | null
    ) => {
      const item = itemRef.current;

      if (!item) {
        return;
      }

      queryClient.cancelQueries({ queryKey: ['product-price'] });

      const baseIngredientCategories = [
        Commerce.IngredientCategory.CHEESE,
        Commerce.IngredientCategory.DOUGH,
        Commerce.IngredientCategory.BASE_SAUCE,
      ];

      let addedFrom = AnalyticsIngredientListName.BaseIngredient;

      if (ingredient) {
        if (baseIngredientCategories.includes(ingredient.ingredientCategoryID)) {
          analyticsEvents.changeBaseIngredient(ingredient.name);
        } else {
          analyticsEvents.addIngredient(ingredient.name, AnalyticsIngredientListName.Garnish);
          addedFrom = AnalyticsIngredientListName.Garnish;
        }
      } else if (currentIngredient) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        baseIngredientCategories.includes(currentIngredient.ingredientCategoryID)
          ? analyticsEvents.removeBaseIngredient(currentIngredient.name)
          : analyticsEvents.removeIngredient(
              currentIngredient.name,
              AnalyticsIngredientListName.Garnish
            );
      }

      const newItem = item.clone();

      // Preserve null (customer explicitly chose "none") vs undefined (reset, not yet chosen)
      newItem.changeSlotIngredient(ingredient ? { ...ingredient, addedFrom } : ingredient, slotId);
      Object.freeze(newItem);
      setItem(newItem);
    },
    [itemRef, queryClient, setItem]
  );
};

export const useSelectSize = (): ((newSize: Commerce.ProductSize) => void) => {
  const { itemRef, setItem } = useContext(ProductContext);
  const queryClient = useQueryClient();

  const { products, isLoading: isProductsLoading } = useAllProductItems();
  const { ingredients, isLoading: isIngredientsLoading } = useAllIngredients();
  const addError = useAddError();

  return useCallback(
    (newSize: Commerce.ProductSize) => {
      if (isProductsLoading || isIngredientsLoading) return;

      const item = itemRef.current;

      if (!item) {
        return;
      }

      const { item: newItem, dosingNotFoundError } = changeOrderItemSize(
        item,
        products,
        ingredients,
        newSize
      );

      queryClient.cancelQueries({ queryKey: ['product-price'] });
      setItem(newItem);

      if (dosingNotFoundError.metadata.ingredients.length > 0) {
        addError(dosingNotFoundError);
      }

      analyticsEvents.selectSize(newItem.getSizeName());
    },
    [
      isProductsLoading,
      isIngredientsLoading,
      itemRef,
      products,
      queryClient,
      setItem,
      ingredients,
      addError,
    ]
  );
};

export const useApplyCampaignRestrictions = (): (() => void) => {
  const { itemRef, setItem } = useContext(ProductContext);

  return useCallback(() => {
    const item = itemRef.current;

    if (!item) {
      return;
    }

    const restrictedItem = item.clone();
    restrictedItem.restrictedModification = true;
    setItem(restrictedItem);
  }, [itemRef, setItem]);
};

type DosingNotFoundError = ErrorMessage<
  errorCode.CLIENT_DOSING_FOR_SIZE_NOT_FOUND,
  { ingredients: IngredientWithDosing[] }
>;

export const changeOrderItemSize = (
  item: OrderItem,
  products: ProductItem[],
  ingredients: IngredientWithDosing[],
  newSize: Commerce.ProductSize
): { item: OrderItem; dosingNotFoundError: DosingNotFoundError } => {
  const dosingNotFoundError = new ErrorMessage(
    errorCode.CLIENT_DOSING_FOR_SIZE_NOT_FOUND,
    {
      ingredients: [],
    },
    ''
  );

  const newProduct = products.find(
    (product) => product.productID === item.productID && product.sizeId === newSize
  );

  if (!newProduct) {
    throw new Error('Product item not found');
  }

  const newItem = OrderItem.fromProductItem(newProduct);
  newItem.uuid = item.uuid;
  newItem.quantity = item.quantity;
  newItem.customized = item.customized;

  newItem.ingredients = item.ingredients
    .map((ingredient) =>
      changeIngredientSize(newProduct, ingredient, newItem.sizeId, ingredients, dosingNotFoundError)
    )
    .filter(Boolean) as IngredientWithDosing[];

  newItem.ingredientSlots.forEach((newSlot) => {
    item.ingredientSlots.forEach((slot) => {
      if (newSlot.categoryId === slot.categoryId) {
        if (slot.selected) {
          const newIngredient = changeIngredientSize(
            newProduct,
            slot.selected,
            newItem.sizeId,
            ingredients,
            dosingNotFoundError
          );
          if (!newIngredient) {
            return;
          }
          newSlot.selected = newIngredient;
        } else {
          newSlot.selected = slot.selected;
        }
        slot.selected = undefined;
      }
    });
  });

  return { item: newItem, dosingNotFoundError };
};

const changeIngredientSize = (
  newProduct: ProductItem,
  ingredient: IngredientWithDosing,
  sizeId: Commerce.ProductSize,
  ingredients: IngredientWithDosing[],
  dosingNotFoundError: DosingNotFoundError
): IngredientWithDosing | null => {
  const correctSizeIngredient = ingredients.find(
    (correctIngredient) =>
      correctIngredient.ingredientID === ingredient.ingredientID &&
      correctIngredient.productSizeId === sizeId
  );

  if (
    !correctSizeIngredient ||
    (correctSizeIngredient.minQuantity == 0 && correctSizeIngredient.maxQuantity == 0)
  ) {
    dosingNotFoundError.metadata.ingredients.push(ingredient);

    const currentSizeName: string[] = newProduct.productSizes
      .filter((size) => size.productSizeID === newProduct.sizeId)
      .map((size) => size.name);

    const errorIngredients = dosingNotFoundError.metadata.ingredients;
    dosingNotFoundError.message =
      `Ei saatavissa ` +
      currentSizeName +
      `-koossa: ${errorIngredients.map((i) => i.name).join(', ')}`;

    return null;
  }

  return {
    ...correctSizeIngredient,
    quantity: ingredient.quantity,
    addedFrom: ingredient.addedFrom,
  };
};
